
import { Vue, Component, Prop } from "vue-property-decorator";
import { openUiPanel } from "@/lib/panels/panels";
import { TreeNode } from "@/types/tree";
import { configStore, globalStore, uiStore } from "@/store/store";
import {
  addPresetToElement,
  addPresetToStoreAndSavePresetsToWordPressIfPresetsPanelIsClosed,
  copyPresetPropertiesToElement,
  createElementPresetFromName,
  removePresetFromElement
} from "@/lib/presets/presets";
import Icon from "@/components/UI/Icon.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import PresetDialog from "@/components/Settings/ElementPresets/PresetDialog.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import AlertBox from "@/components/Reusable/CustomInputs/AlertBox.vue";
import NotificationLogger from "@/lib/notifications/notifications";

@Component({
  components: { NiceButton, Icon, DropdownInput, AlertBox, PresetDialog }
})
export default class ElementPresetChooser extends Vue {
  @Prop()
  element!: TreeNode;

  newPresetName = "";
  autocompleteListIndex = -1;
  addingPreset = false;

  get foundPresetsWithName() {
    if (!this.newPresetName) return true;

    return this.presetItems.filter(preset =>
      preset.text.toLowerCase().includes(this.newPresetName.toLowerCase())
    ).length;
  }

  get elementName() {
    return configStore.element(this.element.data.type).name;
  }

  get availablePresets() {
    return globalStore.presets;
  }

  get isPresetValid() {
    if (!this.currentPreset) return true;
    return (
      this.currentPreset &&
      this.presetItems.find(p => p.value === this.currentPreset)
    );
  }

  get isPresetsOverridesEnabled() {
    return configStore.isExperimentEnabled("design-presets-overrides");
  }

  get presetItems() {
    const type = this.element.data.type;

    return this.availablePresets
      .filter(preset => preset.availableForType === type)
      .map(preset => ({
        text: preset.name,
        value: preset.id
      }));
  }

  get currentPreset(): string | null {
    return this.element.data.properties?.meta?.preset;
  }

  get canCreatePreset() {
    return !!this.element.data.properties?.design;
  }

  async createPresetOnEnter() {
    if (this.autocompleteListIndex !== -1) return;
    if (this.foundPresetsWithName) return;
    if (!this.newPresetName) return;
    if (!this.canCreatePreset) {
      NotificationLogger.log.errorMessage("No design to save as preset");
      return;
    }

    const preset = createElementPresetFromName(
      this.newPresetName,
      this.element.data.type,
      this.element.data.properties.design
    );

    this.addingPreset = true;

    try {
      addPresetToStoreAndSavePresetsToWordPressIfPresetsPanelIsClosed(preset);
      addPresetToElement(preset.id, this.element.id);

      // Blur the input to prevent the dropdown from opening
      // @ts-ignore
      this.$refs.input.blur();
    } catch (error) {
      NotificationLogger.log.errorMessage("Failed to save preset");
    } finally {
      this.addingPreset = false;
    }
  }

  onListIndexUpdate(index: number) {
    this.autocompleteListIndex = index;
  }

  selectPreset(preset: string) {
    this.newPresetName = "";

    addPresetToElement(
      preset,
      this.element.id,
      !this.isPresetsOverridesEnabled
    );

    this.autocompleteListIndex = -1;
  }

  clear() {
    removePresetFromElement(this.element.id);
  }

  detach() {
    if (!this.currentPreset) return;
    copyPresetPropertiesToElement(this.currentPreset, this.element.id);
    removePresetFromElement(this.element.id);
  }

  edit() {
    uiStore.setActivePresetId(this.currentPreset);
    openUiPanel("presets");
  }

  // Preset Dialog
  savePresetDialog = false;
  openSavePresetDialog() {
    this.savePresetDialog = true;
  }

  closePresetDialog() {
    this.savePresetDialog = false;
  }
}
